import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Select from "react-select";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from 'react-toastify';

import coupleImage from '../../../assets/images/couples/Vector.png';
import { SignupContext } from "../../../context/SignupContext";
import { LoginContext } from '../../../context/LoginContext';
import { getDobLimitsForGender, isDobValidForGender } from '../../../utils/dobLimits';
import { getOnboardingPath } from '../../../utils/onboarding';
import { buildGothraOptions, gothraPayloadValue } from '../../../utils/gothra';
import DobFields from '../../common/DobFields';
import { isDisposableEmail } from '../../../utils/tempMailDetector';

const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
const MAX_PHOTO_MB = 5;
const MAX_PHOTOS = 5;

const SignupForm = () => {
  const navigate = useNavigate();

  const { setUserName, setAPIFormData, setUserId, setIsLoggedIn, APIformData, isLoggedIn: signupSession, isOtpSent, setIsOtpSent } = useContext(SignupContext);

  const {
    isLoggedIn,
    userData,
    setIsLoggedIn: setLoginLoggedIn,
    setUserId: setLoginUserId,
    setUserdata,
    setUserName: setLoginUserName,
    setPage
  } = useContext(LoginContext);

  // Client IP state
  const [clientIp, setClientIp] = useState("no ip");
  const getData = async () => {
    try {
      const res = await axios.get("https://api.ipify.org/?format=json");
      setClientIp(res.data.ip);
    } catch (_) {}
  };

  // Photos state
  const [profilePhotos, setProfilePhotos] = useState([]);
  const profilePhotoInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    // Basic details
    name: '',
    gender: '',
    looking_for: '',
    dob: '',
    religion: 'Hindu',
    caste: '',
    subcaste: '',
    profile_for: '',
    language: '',
    height: '',
    weight: '',
    bdy_type: '',
    ps: '',
    ps_details: '',
    eating: '',
    drinking: '',
    smoking: '',
    manglik: '',
    marital_status: '',
    hobbies: [],

    // Education & Career
    highest_education: '',
    ug_degree: '',
    occupation: '',
    emp_in: '',
    annual_income: '',
    org_name: '',

    // Family Details
    sister: '',
    brother: '',
    gothra: null,
    maternalGothra: null,
    familyStatus: '',
    family_type: '',
    livingWithParents: '',
    familyBelongsTo: '',

    // Location
    country: { label: "India", value: '101' },
    state: '',
    city: '',
    pincode: '',

    // Account & Credentials
    mobile: APIformData?.phone || '',
    email: '',
    password: '',
    terms_accepted: true,
    ip: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [options, setOptions] = useState({
    caste: [],
    gothra: [],
    cities: [],
    countries: [],
    income: [],
    language: [],
    states: [],
    subCaste: [],
    education: [],
    occupation: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const genderValue = formData.gender?.value || formData.gender || '';
  const { maxDOB, minDOB } = useMemo(
    () => getDobLimitsForGender(genderValue),
    [genderValue]
  );

  const clearDobIfTooYoung = (nextGender, prevDob) => {
    if (!prevDob) return prevDob;
    return isDobValidForGender(prevDob, nextGender) ? prevDob : '';
  };

  const handleProfilePhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = MAX_PHOTOS - profilePhotos.length;
    if (remainingSlots <= 0) {
      toast.warning(`You can only upload up to ${MAX_PHOTOS} photos.`);
      return;
    }

    const nextFiles = files.slice(0, remainingSlots);
    const validEntries = [];

    for (const file of nextFiles) {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type.toLowerCase())) {
        toast.error(`${file.name}: Only JPG, PNG or GIF files are allowed.`);
        continue;
      }
      if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
        toast.error(`${file.name}: Photo must be under ${MAX_PHOTO_MB}MB.`);
        continue;
      }
      validEntries.push({
        file,
        preview: URL.createObjectURL(file),
      });
    }

    if (validEntries.length > 0) {
      setProfilePhotos((prev) => [...prev, ...validEntries]);
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.profilePhoto;
        return copy;
      });
    }

    e.target.value = '';
  };

  const removeProfilePhoto = (index) => {
    setProfilePhotos((prev) => {
      const target = prev[index];
      if (target?.preview) {
        try {
          URL.revokeObjectURL(target.preview);
        } catch (_) {}
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleNameChange = (e) => {
    const { name, value } = e.target;
    let myName = value;
    if (value.length > 1) {
      const newName = value.split(" ");
      myName = newName.map(str => str.charAt(0).toUpperCase() + str.slice(1)).join(" ");
    }
    setFormData(prev => ({
      ...prev,
      [name]: myName
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newVal
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === 'dob') {
      const gVal = formData.gender?.value || formData.gender;
      if (gVal && newVal) {
        if (!isDobValidForGender(newVal, gVal)) {
          const msg = gVal === 'Male'
            ? 'Male must be at least 21 years old'
            : 'Female must be at least 18 years old';
          setErrors(prev => ({ ...prev, dob: msg }));
        } else {
          setErrors(prev => {
            const next = { ...prev };
            delete next.dob;
            return next;
          });
        }
      }
    }
  };

  const handleLookingForChange = (name, selectedOption) => {
    const nextGender =
      selectedOption.value === 'Bride'
        ? { label: 'Male', value: 'Male' }
        : selectedOption.value === 'Groom'
          ? { label: 'Female', value: 'Female' }
          : { label: 'Others', value: 'Others' };
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
      gender: nextGender,
      dob: clearDobIfTooYoung(nextGender.value, prev.dob),
    }));
    if (errors.looking_for || errors.gender) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy.looking_for;
        delete copy.gender;
        return copy;
      });
    }
  };

  const handleGenderChange = (name, selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption,
      looking_for:
        selectedOption.value === 'Male'
          ? { label: 'Bride', value: 'Bride' }
          : selectedOption.value === 'Female'
            ? { label: 'Groom', value: 'Groom' }
            : { label: 'Bride', value: 'Bride' },
      dob: clearDobIfTooYoung(selectedOption.value, prev.dob),
    }));
    if (errors.gender || errors.looking_for) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy.gender;
        delete copy.looking_for;
        return copy;
      });
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    if (name === 'hobbies') {
      const list = selectedOption || [];
      if (list.length > 10) {
        toast.warning('You can select a maximum of 10 hobbies');
        return;
      }
      setFormData(prev => ({
        ...prev,
        hobbies: list
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.hobbies;
        return next;
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: selectedOption
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const selectMenuProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base) => ({
        ...base,
        borderRadius: 12,
        minHeight: 42,
        borderColor: '#ccc',
        boxShadow: 'none',
      }),
    },
  };

  const handlecountryChange = async (name, selectedOption) => {
    setIsLoading(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/state.php?country=${selectedOption.value}`
      );
      setOptions(prev => ({
        ...prev,
        states: Array.isArray(resp.data?.data?.states) ? resp.data.data.states : [],
      }));
      setFormData(prev => ({
        ...prev,
        [name]: selectedOption,
        state: "",
        city: ""
      }));
    } catch (err) {
      console.error("Error fetching states:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStateChange = async (name, selectedOption) => {
    setIsLoading(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}admin-mat/api/city.php?state=${selectedOption.value}`
      );
      setOptions(prev => ({
        ...prev,
        cities: Array.isArray(resp.data) ? resp.data : (Array.isArray(resp.data?.data) ? resp.data.data : []),
      }));
      setFormData(prev => ({
        ...prev,
        [name]: selectedOption,
        city: ""
      }));
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) newErrors.email = 'Please enter a valid email address';
    else if (isDisposableEmail(formData.email.trim())) newErrors.email = 'Temporary or disposable email addresses are not allowed. Please use a permanent email (e.g., Gmail, Yahoo, Outlook).';
    else if (errors.email) newErrors.email = errors.email;
    
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    else if (!formData.gender) newErrors.dob = 'Select gender before date of birth';
    else if (!isDobValidForGender(formData.dob, formData.gender?.value || formData.gender)) {
      newErrors.dob = formData.gender?.value === 'Male'
        ? 'Male must be at least 21 years old'
        : 'Female must be at least 18 years old';
    }

    if (!formData.looking_for) newErrors.looking_for = 'Please select Looking For';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.caste) newErrors.caste = 'Caste is required';
    if (!formData.profile_for) newErrors.profile_for = 'Please select Profile For';
    if (!formData.language) newErrors.language = 'Please select Mother Tongue / Language';
    if (!formData.height) newErrors.height = 'Please select Height';
    if (!formData.marital_status) newErrors.marital_status = 'Please select Marital Status';
    if (!formData.ps) newErrors.ps = 'Please select Physical Status';
    if ((formData.ps?.value === "Physically Challenged" || formData.ps === "Physically Challenged") && !formData.ps_details?.trim()) {
      newErrors.ps_details = 'Please describe the physical challenge';
    }

    const hobbiesCount = Array.isArray(formData.hobbies) ? formData.hobbies.length : 0;
    if (hobbiesCount > 10) newErrors.hobbies = 'Maximum 10 hobbies allowed';

    if (!formData.highest_education) newErrors.highest_education = 'Please select Highest Education';
    if (!formData.occupation) newErrors.occupation = 'Please select Occupation';
    if (!formData.family_type) newErrors.family_type = 'Please select Family Type';
    if (!formData.state) newErrors.state = 'Please select State';
    if (!formData.country) newErrors.country = 'Country is required';

    const mobileVal = (formData.mobile || APIformData?.phone || '').trim();
    if (!mobileVal) newErrors.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(mobileVal)) newErrors.mobile = 'Please enter a valid 10-digit mobile number starting with 6-9';

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_\-+={}[\]:;"'<>,./~`|\\]).{8,}$/;
    if (!formData.password) newErrors.password = 'Password is required';
    else if (!passwordRegex.test(formData.password)) newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    if (!formData.terms_accepted) newErrors.terms_accepted = 'You must accept terms';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {}
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email) return;

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setErrors(prev => ({
        ...prev,
        email: 'Please enter a valid email address.'
      }));
      return;
    }

    if (isDisposableEmail(email)) {
      setErrors(prev => ({
        ...prev,
        email: 'Temporary or disposable email addresses are not allowed. Please use a permanent email (e.g., Gmail, Yahoo, Outlook).'
      }));
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}Register_controller/check_email_exists`,
        { email }
      );
      if (response.data?.exists || response.data?.status === false || response.data?.response === 'fail' || response.data?.response === 'temp_mail') {
        setErrors(prev => ({
          ...prev,
          email: response.data?.message || 'Email is already registered'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } catch (_) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // 1. First register the basic account
      const mobileVal = (formData.mobile || APIformData?.phone || '').trim();
      const registerPayload = new FormData();
      registerPayload.append('name', formData.name);
      registerPayload.append('email', formData.email);
      registerPayload.append('pwd', formData.password);
      registerPayload.append('mobile', mobileVal);
      registerPayload.append('gender', formData.gender?.value || '');
      registerPayload.append('looking_for', formData.looking_for?.value || '');
      registerPayload.append('dob', formData.dob);
      registerPayload.append('caste', formData.caste?.value || '');
      registerPayload.append('language', formData.language?.value || '');
      registerPayload.append('pro_for', formData.profile_for?.value || '');
      registerPayload.append('high_education', formData.highest_education?.value || '');
      registerPayload.append('occupation', formData.occupation?.value || '');
      registerPayload.append('family_type', formData.family_type?.value || '');
      registerPayload.append('country', formData.country?.label || formData.country?.value || 'India');
      registerPayload.append('state', formData.state?.label || formData.state?.value || '');
      registerPayload.append('height', formData.height?.value || '');
      registerPayload.append('ip', clientIp);

      const hobbiesString = (formData.hobbies || []).map(h => h.value).join(',');
      registerPayload.append('hobbies', hobbiesString);

      let profileBy = "Myself";
      if (formData.profile_for?.value === "Self") profileBy = "Myself";
      else if (formData.profile_for?.value === "Son" || formData.profile_for?.value === "Daughter") profileBy = "My Parents";
      else if (formData.profile_for?.value === "Brother") profileBy = "My Sister";
      else if (formData.profile_for?.value === "Sister") profileBy = "My Brother";
      else if (formData.profile_for?.value === "Friend") profileBy = "My Friend";

      registerPayload.append('pro_for', profileBy);

      const regResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}Register_controller/register`,
        registerPayload
      );

      if (!regResponse.data.status) {
        setErrors({
          api: regResponse.data.message || 'Registration failed',
          ...(regResponse.data.errors || {})
        });
        setIsSubmitting(false);
        return;
      }

      const uid = regResponse.data.id;
      const nextUser = {
        user_id: uid,
        id: uid,
        status: 8,
        name: formData.name,
        gender: formData.gender?.value || '',
        looking_for: formData.looking_for?.value || '',
        phone: mobileVal,
        email: formData.email,
        caste: formData.caste?.value || '',
        language: formData.language?.value || '',
        pro_for: formData.profile_for?.value || '',
        dob: formData.dob,
      };

      setAPIFormData({
        ...APIformData,
        phone: mobileVal,
        user_id: uid,
        id: uid,
        name: formData.name,
        gender: formData.gender?.value || '',
        looking_for: formData.looking_for?.value || '',
        email: formData.email,
        dob: formData.dob,
        caste: formData.caste?.value || '',
        height: formData.height?.value || '',
        highest_education: formData.highest_education?.value || '',
        occupation: formData.occupation?.value || '',
        family_type: formData.family_type?.value || '',
        language: formData.language?.value || '',
        profileFor: formData.profile_for?.value || '',
        hobbies: (formData.hobbies || []).map((h) => h.value),
      });
      setUserName(formData.name);
      setIsLoggedIn(true);
      setIsOtpSent(true);
      setUserId(uid);
      setLoginLoggedIn(true);
      setLoginUserId(uid);
      setUserdata(nextUser);
      if (typeof setLoginUserName === 'function') {
        setLoginUserName(formData.name);
      }

      // 2. Immediately save full profile details via upload_reg
      const uploadPayload = new FormData();
      uploadPayload.append('user_id', uid);
      uploadPayload.append('name', formData.name);
      uploadPayload.append('email', formData.email);
      uploadPayload.append('gender', formData.gender?.value || '');
      uploadPayload.append('looking_for', formData.looking_for?.value || '');
      uploadPayload.append('dob', formData.dob);
      uploadPayload.append('caste', formData.caste?.value || '');
      uploadPayload.append('sub_caste', formData.subcaste?.value || formData.subcaste || '');
      uploadPayload.append('pro_for', formData.profile_for?.value || '');
      uploadPayload.append('language', formData.language?.value || '');
      uploadPayload.append('height', formData.height?.value || '');
      uploadPayload.append('weight', formData.weight?.value || formData.weight || '');
      uploadPayload.append('ms', formData.marital_status?.value || formData.marital_status || '');
      uploadPayload.append('body', formData.bdy_type?.value || formData.bdy_type || '');
      uploadPayload.append('ps', formData.ps?.value || formData.ps || '');
      uploadPayload.append('ps_details', formData.ps_details || '');
      uploadPayload.append('eating', formData.eating?.value || formData.eating || '');
      uploadPayload.append('drinking', formData.drinking?.value || formData.drinking || '');
      uploadPayload.append('smoking', formData.smoking?.value || formData.smoking || '');
      uploadPayload.append('manglik', formData.manglik?.value || formData.manglik || '');
      uploadPayload.append('hobbies', hobbiesString);

      // Education & Career
      uploadPayload.append('highest_edu', formData.highest_education?.value || '');
      uploadPayload.append('ug_degree', formData.ug_degree?.value || formData.ug_degree || '');
      uploadPayload.append('emp_in', formData.emp_in?.value || formData.emp_in || '');
      uploadPayload.append('occupation', formData.occupation?.value || '');
      uploadPayload.append('org_name', formData.org_name || '');
      uploadPayload.append('annual_income', formData.annual_income?.value || formData.annual_income || '');

      // Family
      uploadPayload.append('sister', formData.sister?.value || formData.sister || '');
      uploadPayload.append('brother', formData.brother?.value || formData.brother || '');
      uploadPayload.append('gothra', gothraPayloadValue(formData.gothra));
      uploadPayload.append('gothra_maternal', gothraPayloadValue(formData.maternalGothra));
      uploadPayload.append('family_status', formData.familyStatus?.value || formData.familyStatus || '');
      uploadPayload.append('family_type', formData.family_type?.value || '');
      uploadPayload.append('family_based', formData.familyBelongsTo?.label || formData.familyBelongsTo?.value || formData.familyBelongsTo || '');
      uploadPayload.append('living_wth_parents', formData.livingWithParents?.value || formData.livingWithParents || '');

      // Location
      uploadPayload.append('country', formData.country?.label || formData.country?.value || 'India');
      uploadPayload.append('state', formData.state?.label || formData.state?.value || '');
      uploadPayload.append('city', formData.city?.label || formData.city?.value || formData.city || '');
      uploadPayload.append('pin', formData.pincode || '');

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}Register_controller/upload_reg`,
        uploadPayload
      );

      // 3. Upload attached profile photos if any
      if (profilePhotos.length > 0) {
        for (let i = 0; i < profilePhotos.length; i++) {
          const photo = profilePhotos[i];
          const photoPayload = new FormData();
          photoPayload.append('id', uid);
          photoPayload.append('img', photo.file, photo.file?.name || `photo-${Date.now()}.jpg`);
          photoPayload.append('looking', formData.looking_for?.value || '');
          photoPayload.append('gender', formData.gender?.value || '');
          photoPayload.append('status', 8);
          photoPayload.append('email', formData.email);

          try {
            await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}Register_controller/uploadImage`,
              photoPayload
            );
          } catch (photoErr) {
            console.error('Photo upload error:', photoErr);
          }
        }
      }

      toast.success('Account and profile created successfully!');
      setPage('desired-partner');
      navigate('/desired-partner');
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        api: error.response?.data?.message || error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch options on mount
  useEffect(() => {
    if (isLoggedIn) {
      const next = getOnboardingPath(userData);
      navigate(next || '/recently-joined-matches');
      return;
    }

    if (APIformData?.phone && !formData.mobile) {
      setFormData(prev => ({ ...prev, mobile: APIformData.phone }));
    }

    const fetchOptions = async () => {
      try {
        const [casteRes, eduRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}admin-mat/api/caste.php`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}admin-mat/api/education.php`)
        ]);

        const casteData = casteRes.data?.data || {};
        const eduData = eduRes.data?.data || {};

        setOptions({
          caste: Array.isArray(casteData.caste) ? casteData.caste : [],
          gothra: Array.isArray(casteData.gothra) ? casteData.gothra : [],
          cities: Array.isArray(casteData.cities) ? casteData.cities : [],
          countries: Array.isArray(casteData.countries) ? casteData.countries : [],
          income: Array.isArray(casteData.income) ? casteData.income : [],
          language: Array.isArray(casteData.language) ? casteData.language : [],
          states: Array.isArray(casteData.states) ? casteData.states : [],
          subCaste: Array.isArray(casteData.subcaste) ? casteData.subcaste : [],
          education: Array.isArray(eduData.education) ? eduData.education : [],
          occupation: Array.isArray(eduData.occupation) ? eduData.occupation : [],
        });
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, []);

  return (
    <>
      {isLoading && (
        <div className='custom-loader-g'>
          <div className="spinner-border text-danger" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      <div className='d-none d-md-block py-4 bg-white'></div>

      <div className="page-container">
        <div className="login-container">
          <div className="signup-wrapper" onLoad={getData}>
            <div className="form-section">
              <h2 className="signup-main-title">Create Your Account</h2>
              <p className="subtext">Fill in your details to start your journey of finding the perfect match</p>

              {errors.api && (
                <div className="error-message alert alert-danger text-center mb-3">
                  {errors.api}
                </div>
              )}

              <form className="form" onSubmit={handleSubmit}>
                {/* 1. Photos Upload */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>1</span> Photos (Optional)
                  </h4>
                  <div className="profile-photo-upload-box mb-3">
                    <div className="profile-photo-grid">
                      {profilePhotos.map((photo, index) => (
                        <div className="profile-photo-thumb" key={photo.preview}>
                          <img src={photo.preview} alt={`Preview ${index + 1}`} />
                          {index === 0 && <span className="profile-photo-primary">Profile Pic</span>}
                          <button
                            type="button"
                            className="profile-photo-remove"
                            onClick={() => removeProfilePhoto(index)}
                            aria-label={`Remove photo ${index + 1}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {profilePhotos.length < MAX_PHOTOS && (
                        <label htmlFor="signup-photo-input" className="profile-photo-add">
                          <span className="profile-photo-add-icon">+</span>
                          <span>Add Photo</span>
                        </label>
                      )}
                    </div>
                    <input
                      ref={profilePhotoInputRef}
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                      onChange={handleProfilePhotoChange}
                      className="d-none"
                      id="signup-photo-input"
                    />
                    <p className="profile-photo-hint">
                      Add up to {MAX_PHOTOS} photos · JPG, PNG or GIF · Max {MAX_PHOTO_MB}MB each.
                    </p>
                  </div>
                </div>

                {/* 2. Basic Details */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>2</span> Basic Details
                  </h4>

                  <div className="form-row">
                    <label>
                      <span>Full Name<span className="d-inline coloashe">*</span></span>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleNameChange}
                        placeholder="Enter your name"
                      />
                      {errors.name && <span className="field-error">{errors.name}</span>}
                    </label>

                    <label>
                      <span>Religion<span className="d-inline coloashe">*</span></span>
                      <input
                        readOnly
                        type="text"
                        name="religion"
                        value="Hindu"
                        className="bg-light"
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Gender<span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Male", "Female"].map(val => ({ label: val, value: val }))}
                        value={formData.gender}
                        onChange={(selected) => handleGenderChange("gender", selected)}
                        placeholder="Select Gender"
                        {...selectMenuProps}
                      />
                      {errors.gender && <span className="field-error">{errors.gender}</span>}
                    </label>

                    <label>
                      <span>Looking For <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Bride", "Groom"].map(val => ({ label: val, value: val }))}
                        value={formData.looking_for}
                        onChange={(selected) => handleLookingForChange("looking_for", selected)}
                        placeholder="Looking For"
                        {...selectMenuProps}
                      />
                      {errors.looking_for && <span className="field-error">{errors.looking_for}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Date Of Birth<span className="d-inline coloashe">*</span></span>
                      <DobFields
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        max={maxDOB}
                        min={minDOB}
                        disabled={!genderValue}
                        title={!genderValue ? 'Select gender first' : (genderValue === 'Male' ? 'Must be 21+ years' : 'Must be 18+ years')}
                      />
                      {!genderValue && (
                        <span className="field-error text-muted">Select gender first</span>
                      )}
                      {genderValue && (
                        <span className="field-error text-muted">
                          {genderValue === 'Male' ? 'Male: minimum age 21 years' : 'Female: minimum age 18 years'}
                        </span>
                      )}
                      {errors.dob && <span className="field-error">{errors.dob}</span>}
                    </label>

                    <label>
                      <span>Caste<span className="d-inline coloashe">*</span></span>
                      <Select
                        options={options?.caste?.map(val => ({ label: val, value: val }))}
                        value={formData.caste}
                        onChange={(selected) => handleSelectChange("caste", selected)}
                        placeholder="Select Caste"
                        {...selectMenuProps}
                      />
                      {errors.caste && <span className="field-error">{errors.caste}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Sub Caste</span>
                      <Select
                        options={options?.subCaste?.map(caste => ({ label: caste, value: caste }))}
                        value={formData.subcaste}
                        onChange={(selected) => handleSelectChange("subcaste", selected)}
                        placeholder="Select Sub Caste"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Create Profile For <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Self", "Son", "Daughter", "Brother", "Sister", "Friend"].map(val => ({ label: val, value: val }))}
                        value={formData.profile_for}
                        onChange={(selected) => handleSelectChange("profile_for", selected)}
                        placeholder="Profile For"
                        {...selectMenuProps}
                      />
                      {errors.profile_for && <span className="field-error">{errors.profile_for}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Mother Tongue / Language <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={options?.language?.map(lang => ({ label: lang, value: lang }))}
                        value={formData.language}
                        onChange={(selected) => handleSelectChange("language", selected)}
                        placeholder="Select Language"
                        {...selectMenuProps}
                      />
                      {errors.language && <span className="field-error">{errors.language}</span>}
                    </label>

                    <label>
                      <span>Height (in Feet) <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={Array.from({ length: 4 }, (_, f) => 4 + f)
                          .flatMap(feet =>
                            Array.from({ length: 12 }, (_, inches) => {
                              if (feet === 7 && inches > 0) return null;
                              return { label: `${feet}.${inches} Ft`, value: `${feet}.${inches}` };
                            }).filter(Boolean)
                          )}
                        value={formData.height}
                        onChange={(selected) => handleSelectChange("height", selected)}
                        placeholder="Select Height"
                        {...selectMenuProps}
                      />
                      {errors.height && <span className="field-error">{errors.height}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Marital Status <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Single", "Divorce", (String(genderValue).toLowerCase() === 'male' || formData.looking_for?.value === 'Bride') ? "Widower" : "Widowed"].map(val => ({
                          label: val,
                          value: val
                        }))}
                        value={formData.marital_status}
                        onChange={(selected) => handleSelectChange("marital_status", selected)}
                        placeholder="Select Marital Status"
                        {...selectMenuProps}
                      />
                      {errors.marital_status && <span className="field-error">{errors.marital_status}</span>}
                    </label>

                    <label>
                      <span>Manglik Dosha</span>
                      <Select
                        options={["Yes", "No", "Don't Know"].map(val => ({ label: val, value: val }))}
                        value={formData.manglik}
                        onChange={(selected) => handleSelectChange("manglik", selected)}
                        placeholder="Select Manglik"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Weight (in Kg)</span>
                      <Select
                        options={[...Array.from({ length: 111 }, (_, i) => i + 40).map(val => ({ label: `${val} Kg`, value: `${val}` })), { label: `150+ Kg`, value: `150+` }]}
                        value={formData.weight}
                        onChange={(selected) => handleSelectChange("weight", selected)}
                        placeholder="Select Weight"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Body Type</span>
                      <Select
                        options={["Slim", "Average", "Athletic", "Heavy"].map(val => ({ label: val, value: val }))}
                        value={formData.bdy_type}
                        onChange={(selected) => handleSelectChange("bdy_type", selected)}
                        placeholder="Select Body Type"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Physical Status <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Normal", "Physically Challenged"].map(val => ({ label: val, value: val }))}
                        value={formData.ps}
                        onChange={(selected) => handleSelectChange("ps", selected)}
                        placeholder="Select Physical Status"
                        {...selectMenuProps}
                      />
                      {errors.ps && <span className="field-error">{errors.ps}</span>}
                    </label>

                    <label>
                      <span>Eating Habits</span>
                      <Select
                        options={["Vegetarian", "Non-Vegetarian", "Jain", "Eggetarian", "Vegan"].map(val => ({ label: val, value: val }))}
                        value={formData.eating}
                        onChange={(selected) => handleSelectChange("eating", selected)}
                        placeholder="Select Eating Habits"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  {(formData.ps?.value === "Physically Challenged" || formData.ps === "Physically Challenged") && (
                    <div className="form-row">
                      <label style={{ width: '100%' }}>
                        <span>Describe Physical Challenge <span className="d-inline coloashe">*</span></span>
                        <input
                          type="text"
                          name="ps_details"
                          value={formData.ps_details || ""}
                          onChange={handleChange}
                          placeholder="Describe challenge (e.g. Minor handicap, Visual, Hearing, etc.)"
                        />
                        {errors.ps_details && <span className="field-error">{errors.ps_details}</span>}
                      </label>
                    </div>
                  )}

                  <div className="form-row">
                    <label>
                      <span>Drinking Habits</span>
                      <Select
                        options={["Yes", "No", "Occasionally"].map(val => ({ label: val, value: val }))}
                        value={formData.drinking}
                        onChange={(selected) => handleSelectChange("drinking", selected)}
                        placeholder="Select Drinking Habits"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Smoking Habits</span>
                      <Select
                        options={["Yes", "No", "Occasionally"].map(val => ({ label: val, value: val }))}
                        value={formData.smoking}
                        onChange={(selected) => handleSelectChange("smoking", selected)}
                        placeholder="Select Smoking Habits"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label style={{ width: '100%' }}>
                      <span>Hobbies</span>
                      <small className="text-muted d-block mb-1" style={{ fontSize: '11px' }}>
                        Select up to 10 hobbies (optional) ({(formData.hobbies || []).length}/10 selected)
                      </small>
                      <Select
                        options={[
                          {
                            label: "Creative & Artistic",
                            options: [
                              { value: "drawing", label: "Drawing" },
                              { value: "painting", label: "Painting" },
                              { value: "writing", label: "Writing" },
                              { value: "photography", label: "Photography" },
                              { value: "calligraphy", label: "Calligraphy" },
                              { value: "scrapbooking", label: "Scrapbooking" },
                              { value: "sewing", label: "Sewing" },
                              { value: "knitting", label: "Knitting" },
                              { value: "crocheting", label: "Crocheting" },
                              { value: "woodworking", label: "Woodworking" },
                              { value: "home_decor", label: "Home Decor" },
                            ],
                          },
                          {
                            label: "Performance & Entertainment",
                            options: [
                              { value: "dancing", label: "Dancing" },
                              { value: "singing", label: "Singing" },
                              { value: "playing_music", label: "Playing Musical Instruments" },
                              { value: "acting", label: "Acting" },
                              { value: "vlogging", label: "Vlogging" },
                              { value: "blogging", label: "Blogging" },
                            ],
                          },
                          {
                            label: "Outdoor & Physical",
                            options: [
                              { value: "hiking", label: "Hiking" },
                              { value: "camping", label: "Camping" },
                              { value: "fishing", label: "Fishing" },
                              { value: "gardening", label: "Gardening" },
                              { value: "cycling", label: "Cycling" },
                              { value: "running", label: "Running" },
                              { value: "swimming", label: "Swimming" },
                              { value: "yoga", label: "Yoga" },
                              { value: "meditation", label: "Meditation" },
                            ],
                          },
                          {
                            label: "Tech & Mind",
                            options: [
                              { value: "gaming", label: "Gaming" },
                              { value: "coding", label: "Coding" },
                              { value: "reading", label: "Reading" },
                              { value: "cooking", label: "Cooking" },
                              { value: "traveling", label: "Traveling" },
                              { value: "movies", label: "Watching Movies" },
                              { value: "volunteering", label: "Volunteering" },
                            ],
                          }
                        ]}
                        value={formData.hobbies || []}
                        onChange={(selected) => handleSelectChange("hobbies", selected)}
                        placeholder="Select Hobbies (Optional, Max 10)"
                        isMulti={true}
                        isOptionDisabled={() => (formData.hobbies || []).length >= 10}
                        {...selectMenuProps}
                      />
                      {errors.hobbies && <span className="field-error">{errors.hobbies}</span>}
                    </label>
                  </div>
                </div>

                {/* 3. Education & Career */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>3</span> Education &amp; Career
                  </h4>

                  <div className="form-row">
                    <label>
                      <span>Highest Education <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Not Educated", "10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate", "M Phil", "PHD", "Other"].map(val => ({
                          label: val,
                          value: val
                        }))}
                        value={formData.highest_education}
                        onChange={(selected) => handleSelectChange("highest_education", selected)}
                        placeholder="Select Highest Education"
                        {...selectMenuProps}
                      />
                      {errors.highest_education && <span className="field-error">{errors.highest_education}</span>}
                    </label>

                    <label>
                      <span>Education Qualification</span>
                      <Select
                        options={options?.education?.map(val => ({ label: val, value: val }))}
                        value={formData.ug_degree}
                        onChange={(selected) => handleSelectChange("ug_degree", selected)}
                        placeholder="Select Qualification"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Occupation <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={options?.occupation?.map(res => ({ label: res, value: res }))}
                        value={formData.occupation}
                        onChange={(selected) => handleSelectChange("occupation", selected)}
                        placeholder="Select Occupation"
                        {...selectMenuProps}
                      />
                      {errors.occupation && <span className="field-error">{errors.occupation}</span>}
                    </label>

                    <label>
                      <span>Employed In</span>
                      <Select
                        options={["Private Sector", "Government / Public Sector", "Civil Services", "Defence", "Self-Employed", "Unemployed"].map(val => ({
                          label: val,
                          value: val
                        }))}
                        value={formData.emp_in}
                        onChange={(selected) => handleSelectChange("emp_in", selected)}
                        placeholder="Select Employed In"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Annual Income</span>
                      <Select
                        options={options?.income?.map((val, index) => ({
                          label: val,
                          value: index + 1
                        }))}
                        value={formData.annual_income}
                        onChange={(selected) => handleSelectChange("annual_income", selected)}
                        placeholder="Select Income"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Company / Organization Name</span>
                      <input
                        type="text"
                        name="org_name"
                        value={formData.org_name}
                        onChange={handleChange}
                        placeholder="Company name"
                      />
                    </label>
                  </div>
                </div>

                {/* 4. Family Details */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>4</span> Family Details
                  </h4>

                  <div className="form-row">
                    <label>
                      <span>Sister(s)</span>
                      <Select
                        options={["0", "1", "2", "3", "3+"].map(val => ({ label: val, value: val }))}
                        value={formData.sister}
                        onChange={(selected) => handleSelectChange("sister", selected)}
                        placeholder="Select Sister(s)"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Brother(s)</span>
                      <Select
                        options={["0", "1", "2", "3", "3+"].map(val => ({ label: val, value: val }))}
                        value={formData.brother}
                        onChange={(selected) => handleSelectChange("brother", selected)}
                        placeholder="Select Brother(s)"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Gothra</span>
                      <Select
                        options={buildGothraOptions(options.gothra)}
                        value={formData.gothra}
                        onChange={(selected) => handleSelectChange("gothra", selected)}
                        placeholder="Select Gothra"
                        isDisabled={options.gothra.length === 0}
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Gothra (Maternal)</span>
                      <Select
                        options={buildGothraOptions(options.gothra)}
                        value={formData.maternalGothra}
                        onChange={(selected) => handleSelectChange("maternalGothra", selected)}
                        placeholder="Select Maternal Gothra"
                        isDisabled={options.gothra.length === 0}
                        {...selectMenuProps}
                      />
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Family Status</span>
                      <Select
                        options={["Rich/Affluent", "Upper Middle", "Middle Class", "Lower Middle Class"].map(val => ({
                          label: val,
                          value: val
                        }))}
                        value={formData.familyStatus}
                        onChange={(selected) => handleSelectChange("familyStatus", selected)}
                        placeholder="Select Family Status"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Family Type <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={["Joint Family", "Nuclear Family"].map(val => ({ label: val, value: val }))}
                        value={formData.family_type}
                        onChange={(selected) => handleSelectChange("family_type", selected)}
                        placeholder="Select Family Type"
                        {...selectMenuProps}
                      />
                      {errors.family_type && <span className="field-error">{errors.family_type}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>Living with Parents?</span>
                      <Select
                        options={["Yes", "No", "Not Applicable"].map(val => ({ label: val, value: val }))}
                        value={formData.livingWithParents}
                        onChange={(selected) => handleSelectChange("livingWithParents", selected)}
                        placeholder="Select Living with Parents"
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Family Belongs To (Native State)</span>
                      <Select
                        options={options?.states?.map(val => ({
                          label: Object.values(val).toString(),
                          value: Object.values(val).toString()
                        }))}
                        value={formData.familyBelongsTo}
                        onChange={(selected) => handleSelectChange("familyBelongsTo", selected)}
                        placeholder="Select Native State"
                        {...selectMenuProps}
                      />
                    </label>
                  </div>
                </div>

                {/* 5. Location Details */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>5</span> Location Details
                  </h4>

                  <div className="form-row">
                    <label>
                      <span>Country <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={options?.countries?.map((o) => ({
                          label: Object.values(o).toString(),
                          value: Object.keys(o).toString()
                        }))}
                        value={formData.country}
                        onChange={(selected) => handlecountryChange("country", selected)}
                        placeholder="Select Country"
                        {...selectMenuProps}
                      />
                      {errors.country && <span className="field-error">{errors.country}</span>}
                    </label>

                    <label>
                      <span>State <span className="d-inline coloashe">*</span></span>
                      <Select
                        options={options?.states?.map(val => ({
                          label: Object.values(val).toString(),
                          value: Object.keys(val).toString()
                        }))}
                        value={formData.state || null}
                        onChange={(selected) => handleStateChange("state", selected)}
                        placeholder="Select State"
                        {...selectMenuProps}
                      />
                      {errors.state && <span className="field-error">{errors.state}</span>}
                    </label>
                  </div>

                  <div className="form-row">
                    <label>
                      <span>City</span>
                      <Select
                        options={options?.cities?.map(city => ({
                          label: typeof city === 'object' ? Object.values(city).toString() : String(city),
                          value: typeof city === 'object' ? Object.values(city).toString() : String(city)
                        }))}
                        value={formData.city || null}
                        onChange={(selected) => handleSelectChange("city", selected)}
                        placeholder="Select City"
                        isDisabled={options?.cities?.length === 0}
                        {...selectMenuProps}
                      />
                    </label>

                    <label>
                      <span>Pin Code</span>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        placeholder="Enter Pin Code"
                      />
                    </label>
                  </div>
                </div>

                {/* 6. Account Credentials & Terms */}
                <div className="form-section-block">
                  <h4 className="section-block-title">
                    <span>6</span> Account &amp; Credentials
                  </h4>

                  <div className="form-row">
                    <label>
                      <span>Mobile Number <span className="d-inline coloashe">*</span></span>
                      <input
                        type="tel"
                        name="mobile"
                        inputMode="numeric"
                        maxLength={10}
                        value={formData.mobile || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFormData(prev => ({ ...prev, mobile: val }));
                          if (errors.mobile) {
                            setErrors(prev => {
                              const copy = { ...prev };
                              delete copy.mobile;
                              return copy;
                            });
                          }
                        }}
                        placeholder="10-digit mobile number"
                        autoComplete="tel"
                        required
                      />
                      {errors.mobile && <span className="field-error">{errors.mobile}</span>}
                    </label>

                    <label>
                      <span>Email ID <span className="d-inline coloashe">*</span></span>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        placeholder="Enter your email"
                        className="email-input"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                        style={{ textTransform: 'none' }}
                        required
                      />
                      {errors.email && <span className="field-error">{errors.email}</span>}
                    </label>
                  </div>

                  <div className="form-group mb-3">
                    <label className="mb-2">
                      <span>Password <span className="d-inline coloashe">*</span></span>
                    </label>
                    <div className="input-group mb-0">
                      <input
                        style={{ flex: 1, marginTop: 0, textTransform: 'none' }}
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min 8 chars (upper, lower, number, special)"
                        aria-label="Enter your password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                      <span
                        className="input-group-text"
                        onClick={() => setShowPassword(prev => !prev)}
                        style={{ cursor: 'pointer', borderRadius: "0 12px 12px 0" }}
                      >
                        {showPassword ? <FaEye size={20} /> : <FaEyeSlash size={20} />}
                      </span>
                    </div>
                    {errors.password && <span className="field-error">{errors.password}</span>}
                    {formData.password && (
                      <div style={{ fontSize: '11px', marginTop: '6px', color: '#666', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ color: formData.password.length >= 8 ? '#28a745' : '#888' }}>
                          {formData.password.length >= 8 ? '✓' : '○'} 8+ chars
                        </span>
                        <span style={{ color: /[A-Z]/.test(formData.password) ? '#28a745' : '#888' }}>
                          {/[A-Z]/.test(formData.password) ? '✓' : '○'} 1 Uppercase
                        </span>
                        <span style={{ color: /[a-z]/.test(formData.password) ? '#28a745' : '#888' }}>
                          {/[a-z]/.test(formData.password) ? '✓' : '○'} 1 Lowercase
                        </span>
                        <span style={{ color: /\d/.test(formData.password) ? '#28a745' : '#888' }}>
                          {/\d/.test(formData.password) ? '✓' : '○'} 1 Number
                        </span>
                        <span style={{ color: /[@$!%*?&#^()_\-+={}[\]:;"'<>,./~`|\\]/.test(formData.password) ? '#28a745' : '#888' }}>
                          {/[@$!%*?&#^()_\-+={}[\]:;"'<>,./~`|\\]/.test(formData.password) ? '✓' : '○'} 1 Special
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="checkbox">
                    <input
                      type="checkbox"
                      id="terms"
                      name="terms_accepted"
                      checked={formData.terms_accepted}
                      onChange={handleChange}
                    />
                    <label htmlFor="terms">
                      I hereby agree to all the contents of the{" "}
                      <a
                        href="/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#ff4d6d", margin: "0 4px", textDecoration: "none", fontWeight: 600 }}
                      >
                        Terms of Use
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#ff4d6d", margin: "0 4px", textDecoration: "none", fontWeight: 600 }}
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                  {errors.terms_accepted && (
                    <div className="field-error mb-2">{errors.terms_accepted}</div>
                  )}
                </div>

                <button
                  type="submit"
                  className="create-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating Account & Profile...' : 'Create Account'}
                </button>
              </form>

              <p className="login-text">
                Already have an account? <Link to="/login">Log In</Link>
              </p>
            </div>

            <div className="image-section d-none d-lg-block">
              <img src={coupleImage} alt="Couple" />
              <div className="overlay-text">
                <h2 className="text-white">
                  Your Journey To Love Begins <span className="highlight">Here !</span>
                </h2>
                <p className='fw-bold text-center'>
                  Connect With Genuine Matches Who <br />
                  Share Your <span className="highlight">Values</span> And <span className="highlight">Dreams.</span>
                </p>
              </div>
            </div>

            <style>
              {`
                html, body {
                  overflow-x: clip !important;
                }

                .page-container {
                  width: 100%;
                  min-height: 100vh;
                  display: flex;
                  justify-content: center;
                  align-items: flex-start;
                  background-color: #fbf7f6;
                  padding: 24px 16px 48px;
                  overflow: visible !important;
                }

                .login-container {
                  width: 100%;
                  max-width: 1260px;
                  background-color: #ffffff;
                  border-radius: 24px;
                  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.06);
                  overflow: visible !important;
                }

                .signup-wrapper {
                  display: grid;
                  grid-template-columns: 1.25fr 0.85fr;
                  gap: 24px;
                  align-items: start;
                  font-family: 'Outfit', 'Plus Jakarta Sans', -apple-system, sans-serif;
                  position: relative;
                  width: 100%;
                  overflow: visible !important;
                }

                .form-section {
                  min-width: 0;
                  padding: 36px 36px 48px;
                  display: flex;
                  flex-direction: column;
                  justify-content: flex-start;
                }

                .signup-main-title {
                  font-size: 1.95rem;
                  font-weight: 700;
                  color: #1e242d;
                  margin-bottom: 6px;
                  text-align: center;
                }

                .subtext {
                  font-size: 0.92rem;
                  color: #667085;
                  margin-bottom: 24px;
                  text-align: center;
                }

                .form-section-block {
                  background: #fdfbfb;
                  border: 1px solid #f0e6e6;
                  border-radius: 14px;
                  padding: 20px 22px;
                  margin-bottom: 22px;
                }

                .section-block-title {
                  font-size: 1.1rem;
                  font-weight: 700;
                  color: #2b354f;
                  margin: 0 0 16px;
                  display: flex;
                  align-items: center;
                  gap: 10px;
                }

                .section-block-title span {
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  width: 26px;
                  height: 26px;
                  border-radius: 50%;
                  background: #e53d5c;
                  color: #fff;
                  font-size: 0.82rem;
                  font-weight: 700;
                }

                .form-row {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 14px;
                }

                .form-row.form-row--full,
                .form-row label[style*="width: 100%"] {
                  grid-column: 1 / -1;
                }

                .form label {
                  display: flex;
                  flex-direction: column;
                  font-size: 13.5px;
                  font-weight: 600;
                  color: #334155;
                  margin-bottom: 12px;
                  min-width: 0;
                }

                .form input, .form select {
                  padding: 10px 14px;
                  margin-top: 5px;
                  width: 100%;
                  border-radius: 12px;
                  border: 1px solid #ccc;
                  font-size: 14.5px;
                  background: white;
                  outline: none;
                  box-sizing: border-box;
                  transition: border-color 0.2s;
                }

                .form input:focus {
                  border-color: #e53d5c;
                }

                .coloashe {
                  color: #e53d5c;
                  margin-left: 2px;
                }

                .field-error {
                  color: #e53d5c;
                  font-size: 12px;
                  font-weight: 500;
                  margin-top: 4px;
                }

                /* Photo Upload Grid */
                .profile-photo-grid {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 12px;
                  margin-top: 8px;
                }

                .profile-photo-thumb {
                  width: 86px;
                  height: 86px;
                  border-radius: 12px;
                  overflow: hidden;
                  position: relative;
                  border: 2px solid #e53d5c;
                  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
                }

                .profile-photo-thumb img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                }

                .profile-photo-primary {
                  position: absolute;
                  bottom: 0;
                  left: 0;
                  right: 0;
                  background: rgba(229, 61, 92, 0.88);
                  color: #fff;
                  font-size: 9px;
                  font-weight: 700;
                  text-align: center;
                  padding: 2px 0;
                  text-transform: uppercase;
                }

                .profile-photo-remove {
                  position: absolute;
                  top: 3px;
                  right: 3px;
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background: rgba(0, 0, 0, 0.65);
                  color: #fff;
                  border: none;
                  font-size: 13px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                }

                .profile-photo-add {
                  width: 86px;
                  height: 86px;
                  border-radius: 12px;
                  border: 2px dashed #d4a373;
                  background: #fffdfa;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  gap: 4px;
                  color: #b8860b;
                  font-size: 12px;
                  font-weight: 600;
                  transition: all 0.2s;
                }

                .profile-photo-add:hover {
                  background: #fff8f0;
                  border-color: #b8860b;
                }

                .profile-photo-add-icon {
                  font-size: 20px;
                  line-height: 1;
                  font-weight: 700;
                }

                .profile-photo-hint {
                  font-size: 12px;
                  color: #64748b;
                  margin: 8px 0 0;
                }

                /* Checkbox - Clean Inline Row */
                .form .checkbox {
                  display: flex !important;
                  flex-direction: row !important;
                  align-items: center !important;
                  gap: 12px !important;
                  margin: 16px 0 20px !important;
                  padding: 0 !important;
                  width: 100% !important;
                }

                .form .checkbox input[type="checkbox"] {
                  width: 18px !important;
                  height: 18px !important;
                  min-width: 18px !important;
                  margin: 0 !important;
                  accent-color: #e53d5c !important;
                  cursor: pointer;
                  flex-shrink: 0;
                }

                .form .checkbox label {
                  display: inline !important;
                  flex-direction: row !important;
                  font-size: 13.5px !important;
                  font-weight: 500 !important;
                  color: #475569 !important;
                  margin: 0 !important;
                  line-height: 1.5 !important;
                  cursor: pointer;
                  flex: 1;
                }

                .create-btn {
                  padding: 14px;
                  background: linear-gradient(135deg, #e53d5c 0%, #c9184a 100%);
                  color: white;
                  border: none;
                  border-radius: 12px;
                  font-weight: 700;
                  font-size: 1.05rem;
                  cursor: pointer;
                  width: 100%;
                  box-shadow: 0 4px 16px rgba(229, 61, 92, 0.3);
                  transition: all 0.25s ease;
                }

                .create-btn:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 6px 20px rgba(229, 61, 92, 0.4);
                }

                .create-btn:disabled {
                  background: #ccc;
                  box-shadow: none;
                  cursor: not-allowed;
                }

                .login-text {
                  text-align: center;
                  margin-top: 20px;
                  font-size: 14px;
                  color: #475569;
                }

                .login-text a {
                  color: #e53d5c;
                  text-decoration: none;
                  font-weight: 700;
                }

                /* Sticky Right Banner on Desktop */
                .image-section {
                  position: -webkit-sticky !important;
                  position: sticky !important;
                  top: 24px !important;
                  align-self: start !important;
                  height: calc(100vh - 48px);
                  max-height: 640px;
                  min-height: 480px;
                  border-radius: 20px;
                  margin: 32px 24px 32px 0;
                  overflow: hidden;
                  background: #fce6ea;
                  box-shadow: 0 10px 30px rgba(229, 61, 92, 0.12);
                  z-index: 10;
                }

                .image-section img {
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  object-position: center 20%;
                  display: block;
                }

                .image-section::after {
                  content: '';
                  position: absolute;
                  inset: 0;
                  background: linear-gradient(180deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.25) 45%, rgba(0, 0, 0, 0.85) 100%);
                  pointer-events: none;
                }

                .overlay-text {
                  position: absolute;
                  bottom: 28px;
                  left: 0;
                  right: 0;
                  color: white;
                  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.7);
                  padding: 20px 24px;
                  text-align: center;
                  z-index: 2;
                }

                .overlay-text h2 {
                  font-size: 1.75rem;
                  font-weight: 700;
                  line-height: 1.35;
                  margin-bottom: 10px;
                }

                .overlay-text p {
                  font-size: 14.5px;
                  line-height: 1.5;
                  color: white !important;
                  margin: 0;
                }

                .highlight {
                  color: #ffc107;
                }

                /* Responsive Breakpoints */
                @media screen and (max-width: 1200px) {
                  .signup-wrapper {
                    grid-template-columns: 1.2fr 0.8fr;
                    gap: 16px;
                  }
                  .form-section {
                    padding: 28px 24px 40px;
                  }
                  .image-section {
                    margin: 24px 16px 24px 0;
                  }
                }

                @media screen and (max-width: 991px) {
                  .page-container {
                    padding: 12px 12px 32px;
                  }

                  .signup-wrapper {
                    grid-template-columns: 1fr;
                    gap: 0;
                  }

                  .image-section {
                    order: -1;
                    width: 100%;
                    height: 240px;
                    max-height: 240px;
                    border-radius: 16px 16px 0 0;
                    margin: 0;
                    position: relative !important;
                    top: 0 !important;
                  }

                  .image-section img {
                    object-position: center 20%;
                  }

                  .overlay-text {
                    bottom: 15px;
                    padding: 16px;
                  }

                  .overlay-text h2 {
                    font-size: 1.35rem;
                    margin-bottom: 6px;
                  }

                  .overlay-text p {
                    font-size: 13px;
                  }

                  .form-section {
                    padding: 24px 16px;
                  }
                }

                @media screen and (max-width: 640px) {
                  .page-container {
                    padding: 0 0 24px 0;
                    background: #ffffff;
                  }

                  .login-container {
                    border-radius: 0;
                    box-shadow: none;
                  }

                  .image-section {
                    height: 200px;
                    border-radius: 0;
                  }

                  .form-section {
                    padding: 18px 12px 28px;
                  }

                  .signup-main-title {
                    font-size: 1.5rem;
                  }

                  .subtext {
                    font-size: 0.85rem;
                    margin-bottom: 16px;
                  }

                  .form-row {
                    grid-template-columns: 1fr;
                    gap: 0;
                  }

                  .form-section-block {
                    padding: 16px 12px;
                    margin-bottom: 16px;
                    border-radius: 12px;
                  }

                  .section-block-title {
                    font-size: 1rem;
                    margin-bottom: 12px;
                  }

                  .profile-photo-thumb,
                  .profile-photo-add {
                    width: 68px;
                    height: 68px;
                  }

                  .profile-photo-add-icon {
                    font-size: 16px;
                  }

                  .profile-photo-add span:last-child {
                    font-size: 10px;
                  }
                }
              `}
            </style>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupForm;